import React, { useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { projects } from '../data/projects';

// Resizable Image Modal Component
const ResizableImageModal = ({ image, isOpen, onClose, projectTitle }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  if (!isOpen) return null;

  const handleMouseDown = (e) => {
    if (e.target.tagName === 'IMG') {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.25));
  };

  const handleReset = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* Control Panel */}
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        display: 'flex',
        gap: '10px',
        zIndex: 1001
      }}>
        <button
          onClick={handleZoomOut}
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            border: 'none',
            borderRadius: '6px',
            padding: '10px 15px',
            cursor: 'pointer',
            fontSize: '18px',
            fontWeight: 'bold'
          }}
        >
          −
        </button>
        <button
          onClick={handleZoomIn}
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            border: 'none',
            borderRadius: '6px',
            padding: '10px 15px',
            cursor: 'pointer',
            fontSize: '18px',
            fontWeight: 'bold'
          }}
        >
          +
        </button>
        <button
          onClick={handleReset}
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            border: 'none',
            borderRadius: '6px',
            padding: '10px 15px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          Reset
        </button>
        <button
          onClick={handleClose}
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            border: 'none',
            borderRadius: '6px',
            padding: '10px 15px',
            cursor: 'pointer',
            fontSize: '18px',
            fontWeight: 'bold'
          }}
        >
          ×
        </button>
      </div>

      {/* Image Container */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        maxWidth: '90vw',
        maxHeight: '90vh'
      }}>
        <img
          src={image.src}
          alt={image.alt || `${projectTitle} screenshot`}
          style={{
            transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
            transition: isDragging ? 'none' : 'transform 0.2s ease',
            maxWidth: '90vw',
            maxHeight: '80vh',
            objectFit: 'contain',
            borderRadius: '8px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
            cursor: isDragging ? 'grabbing' : 'grab'
          }}
          onMouseDown={handleMouseDown}
          draggable={false}
        />
        
        {/* Caption */}
        {image.caption && (
          <p style={{
            color: 'white',
            fontSize: '16px',
            textAlign: 'center',
            marginTop: '20px',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            padding: '10px 20px',
            borderRadius: '6px',
            maxWidth: '600px'
          }}>
            {image.caption}
          </p>
        )}
      </div>

      {/* Scale Indicator */}
      <div style={{
        position: 'absolute',
        bottom: '20px',
        left: '20px',
        color: 'white',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        padding: '8px 16px',
        borderRadius: '6px',
        fontSize: '14px'
      }}>
        {Math.round(scale * 100)}%
      </div>
    </div>
  );
};

const ProjectPage = () => {
  const { slug } = useParams();
  const project = projects.find(p => p.slug === slug);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [galleryImageSize, setGalleryImageSize] = useState(1); // 1 = 100% (default size)

  if (!project) {
    return <Navigate to="/" replace />;
  }

  const openImageModal = (image) => {
    setSelectedImage(image);
    setIsModalOpen(true);
  };

  const closeImageModal = () => {
    setSelectedImage(null);
    setIsModalOpen(false);
  };

  const handleGallerySizeIncrease = () => {
    setGalleryImageSize(prev => Math.min(prev + 0.25, 2)); // Max 200%
  };

  const handleGallerySizeDecrease = () => {
    setGalleryImageSize(prev => Math.max(prev - 0.25, 0.5)); // Min 50%
  };

  const resetGallerySize = () => {
    setGalleryImageSize(1);
  };

  const containerStyle = {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '40px 20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    lineHeight: '1.7'
  };

  const headerStyle = {
    borderBottom: '3px solid #2563eb',
    paddingBottom: '30px',
    marginBottom: '40px'
  };

  const titleStyle = {
    fontSize: '2.5rem',
    fontWeight: '700',
    color: '#1f2937',
    margin: '0 0 15px 0',
    lineHeight: '1.2'
  };

  const metaStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
    fontSize: '14px',
    color: '#6b7280',
    marginBottom: '20px'
  };

  const statusStyle = {
    ...metaStyle,
    color: project.status === 'Completed' ? '#059669' : '#d97706',
    fontWeight: '600'
  };

  const techContainerStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '10px'
  };

  const techBadgeStyle = {
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '500'
  };

  const sectionStyle = {
    marginBottom: '40px'
  };

  const sectionTitleStyle = {
    fontSize: '1.5rem',
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: '15px',
    borderLeft: '4px solid #2563eb',
    paddingLeft: '15px'
  };

  const contentStyle = {
    fontSize: '16px',
    color: '#374151',
    lineHeight: '1.8',
    marginBottom: '20px'
  };

  const highlightBoxStyle = {
    backgroundColor: '#f0f9ff',
    border: '1px solid #0ea5e9',
    borderRadius: '8px',
    padding: '20px',
    margin: '20px 0'
  };

  const backButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    textDecoration: 'none',
    padding: '10px 20px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    border: 'none',
    cursor: 'pointer'
  };

  const imageStyle = {
    width: '100%',
    maxWidth: `${600 * galleryImageSize}px`,
    height: 'auto',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    margin: '20px 0',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    transform: `scale(${galleryImageSize})`,
    transformOrigin: 'center top'
  };

  const imageHoverStyle = {
    transform: 'scale(1.02)',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)'
  };

  return (
    <>
      <ResizableImageModal 
        image={selectedImage}
        isOpen={isModalOpen}
        onClose={closeImageModal}
        projectTitle={project.title}
      />
      <div style={containerStyle}>
      {/* Back Button */}
      <button 
        style={backButtonStyle}
        onClick={() => window.history.back()}
        onMouseOver={(e) => e.target.style.backgroundColor = '#e5e7eb'}
        onMouseOut={(e) => e.target.style.backgroundColor = '#f3f4f6'}
      >
        ← Back to Projects
      </button>

      {/* Header Section */}
      <header style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', marginBottom: '20px' }}>
          <div style={{ fontSize: '60px' }}>{project.image}</div>
          <div style={{ flex: 1 }}>
            <h1 style={titleStyle}>{project.title}</h1>
            <div style={metaStyle}>
              <span>📅 {project.date}</span>
              <span>🏷️ {project.category}</span>
              <span style={statusStyle}>● {project.status}</span>
              {project.featured && (
                <span style={{ 
                  backgroundColor: '#fbbf24', 
                  color: '#92400e', 
                  padding: '4px 12px', 
                  borderRadius: '15px', 
                  fontSize: '11px',
                  fontWeight: 'bold'
                }}>
                  FEATURED PROJECT
                </span>
              )}
            </div>
          </div>
        </div>

        <div style={techContainerStyle}>
          {project.technologies.map((tech, index) => (
            <span key={index} style={techBadgeStyle}>
              {tech}
            </span>
          ))}
        </div>
      </header>

      {/* Project Images Section */}
      {project.images && project.images.length > 0 && (
        <section style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Project Gallery</h2>
          <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '10px', fontStyle: 'italic' }}>
            💡 Click on any image to view it in full size with zoom and pan controls
          </p>
          
          {/* Gallery Size Controls */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            marginBottom: '25px',
            padding: '15px',
            backgroundColor: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
              Gallery Size:
            </span>
            <button
              onClick={handleGallerySizeDecrease}
              style={{
                backgroundColor: '#e2e8f0',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#374151',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#cbd5e1'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#e2e8f0'}
            >
              −
            </button>
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#1f2937',
              minWidth: '60px',
              textAlign: 'center'
            }}>
              {Math.round(galleryImageSize * 100)}%
            </div>
            <button
              onClick={handleGallerySizeIncrease}
              style={{
                backgroundColor: '#e2e8f0',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#374151',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#cbd5e1'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#e2e8f0'}
            >
              +
            </button>
            <button
              onClick={resetGallerySize}
              style={{
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#1d4ed8'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#2563eb'}
            >
              Reset
            </button>
          </div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: `repeat(auto-fit, minmax(${Math.max(200, 300 * galleryImageSize)}px, 1fr))`, 
            gap: '20px' 
          }}>
            {project.images.map((image, index) => (
              <div key={index}>
                <img 
                  src={image.src} 
                  alt={image.alt || `${project.title} screenshot ${index + 1}`}
                  style={imageStyle}
                  onClick={() => openImageModal(image)}
                  onMouseOver={(e) => {
                    e.target.style.transform = imageHoverStyle.transform;
                    e.target.style.boxShadow = imageHoverStyle.boxShadow;
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.boxShadow = imageStyle.boxShadow;
                  }}
                />
                {image.caption && (
                  <p style={{ fontSize: '14px', color: '#6b7280', textAlign: 'center', marginTop: '8px' }}>
                    {image.caption}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Description Section */}
      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Project Overview</h2>
        <p style={contentStyle}>{project.description}</p>
        {project.detailedDescription && (
          <div dangerouslySetInnerHTML={{ __html: project.detailedDescription }} style={contentStyle} />
        )}
      </section>

      {/* Challenges Section - Only render if challenges exist */}
      {(project.challenges || project.detailedChallenges) && (
        <section style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Challenges & Solutions</h2>
          {project.challenges && (
            <p style={contentStyle}>{project.challenges}</p>
          )}
          {project.detailedChallenges && (
            <div dangerouslySetInnerHTML={{ __html: project.detailedChallenges }} style={contentStyle} />
          )}
        </section>
      )}

      {/* Results Section - Only render if results exist */}
      {(project.results || project.detailedResults) && (
        <section style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Results & Impact</h2>
          {project.results && (
            <div style={highlightBoxStyle}>
              <p style={{ ...contentStyle, marginBottom: 0 }}>{project.results}</p>
            </div>
          )}
          {project.detailedResults && (
            <div dangerouslySetInnerHTML={{ __html: project.detailedResults }} style={contentStyle} />
          )}
        </section>
      )}

      {/* Technical Details Section */}
      {project.technicalDetails && (
        <section style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Technical Implementation</h2>
          <div dangerouslySetInnerHTML={{ __html: project.technicalDetails }} style={contentStyle} />
        </section>
      )}

      {/* Links Section */}
      {project.links && project.links.length > 0 && (
        <section style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Project Links</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
            {project.links.map((link, index) => (
              <a 
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  backgroundColor: '#2563eb',
                  color: 'white',
                  textDecoration: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#1d4ed8'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#2563eb'}
              >
                {link.type === 'github' && '📱 '}
                {link.type === 'demo' && '🌐 '}
                {link.type === 'docs' && '📚 '}
                {link.label}
              </a>
            ))}
          </div>
        </section>
      )}
      </div>
    </>
  );
};

export default ProjectPage;
